import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';

import { InfoPopover } from './InfoPopover';

interface FinanceiroPageHeaderProps {
  /** Texto pequeno em CAIXA ALTA acima do título (a seção/área). Ex.: "Resultado". */
  eyebrow?: string;
  titulo: string;
  subtitulo?: string;
  /** Ícone Phosphor já dimensionado (sugestão: size=24 weight="fill"). */
  icon: React.ReactNode;
  /** Cor-tema da tela (a mesma do card no hub). */
  cor: string;
  /** Infotip ao lado do título: explica a tela / como ler os números. */
  info?: React.ReactNode;
  infoTitle?: string;
  /** Slot à direita (ex.: botão Exportar, toggle de escopo). */
  actions?: React.ReactNode;
  backTo?: string;
}

export const FinanceiroPageHeader = ({
  eyebrow,
  titulo,
  subtitulo,
  icon,
  cor,
  info,
  infoTitle,
  actions,
  backTo = '/modulesHome/financeiro',
}: FinanceiroPageHeaderProps) => {
  const navigate = useNavigate();
  // Preserva a querystring (empresa/período da Auditoria, filtros do Financeiro)
  // ao voltar — senão o contexto da topbar global "reseta".
  const { search } = useLocation();
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Flex align="center" gap={3} mb={{ base: 5, md: 7 }}>
        <Flex
          as="button"
          aria-label="Voltar"
          onClick={() => navigate({ pathname: backTo, search })}
          align="center"
          justify="center"
          w="34px"
          h="34px"
          borderRadius="lg"
          color="#64748B"
          flexShrink={0}
          transition="all 0.15s"
          _hover={{ bg: 'rgba(15,23,42,0.06)', color: '#0F172A' }}
        >
          <ArrowLeft size={20} />
        </Flex>

        {/* Badge de ícone na cor da tela (assinatura cromática) */}
        <Flex
          align="center"
          justify="center"
          w="48px"
          h="48px"
          borderRadius="16px"
          bgGradient={`linear(to-br, ${cor}, ${cor}AF)`}
          color="white"
          boxShadow={`0 12px 26px -8px ${cor}99`}
          flexShrink={0}
        >
          {icon}
        </Flex>

        <Box minW={0}>
          {eyebrow && (
            <Text
              fontSize="11px"
              fontWeight={800}
              color={cor}
              textTransform="uppercase"
              letterSpacing="0.12em"
              lineHeight="1.2"
              mb="2px"
            >
              {eyebrow}
            </Text>
          )}
          <Flex align="center" gap={1.5} minW={0}>
            <Text
              fontSize={{ base: '21px', md: '27px' }}
              fontWeight={800}
              color="#0F172A"
              lineHeight="1.1"
              letterSpacing="-0.01em"
              noOfLines={1}
            >
              {titulo}
            </Text>
            {info && (
              <Box flexShrink={0}>
                <InfoPopover title={infoTitle ?? titulo} size={17}>
                  {info}
                </InfoPopover>
              </Box>
            )}
          </Flex>
          {subtitulo && (
            <Text fontSize="sm" color="#64748B" noOfLines={1} mt="1px">
              {subtitulo}
            </Text>
          )}
        </Box>

        {actions && (
          <Flex ml="auto" align="center" gap={2} flexShrink={0}>
            {actions}
          </Flex>
        )}
      </Flex>
    </motion.div>
  );
};
